import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { reportsApi, usersApi } from '@/api/endpoints'
import type { ReportSummary, User } from '@/api/types'
import { useAuthStore } from '@/stores/useAuthStore'
import { Button, Card, Field, Input, Select } from '@/components/ui'
import { formatHours, formatMoney } from '@/utils/format'

dayjs.extend(isoWeek) // týden začíná pondělím

const DATE = 'YYYY-MM-DD'

/** Rychlá období nad filtrem — pojmenovaná tak, jak se o nich mluví. */
const PRESETS: { label: string; range: () => [string, string] }[] = [
  {
    label: 'Tento týden',
    range: () => [dayjs().startOf('isoWeek').format(DATE), dayjs().endOf('isoWeek').format(DATE)],
  },
  {
    label: 'Tento měsíc',
    range: () => [dayjs().startOf('month').format(DATE), dayjs().endOf('month').format(DATE)],
  },
  {
    label: 'Předchozí měsíc',
    range: () => {
      const prev = dayjs().subtract(1, 'month')
      return [prev.startOf('month').format(DATE), prev.endOf('month').format(DATE)]
    },
  },
]

export default function ReportsPage() {
  const { user } = useAuthStore()
  const [from, setFrom] = useState(() => PRESETS[1].range()[0]) // výchozí: tento měsíc
  const [to, setTo] = useState(() => PRESETS[1].range()[1])
  const [userId, setUserId] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [data, setData] = useState<ReportSummary | null>(null)

  useEffect(() => {
    if (user?.isAdmin) usersApi.list().then(setUsers)
  }, [user])

  const run = (f = from, t = to) => {
    reportsApi.summary(f, t, userId || undefined).then(setData)
  }

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    const [f, t] = preset.range()
    setFrom(f)
    setTo(t)
    run(f, t) // setState je asynchronní, rozsah proto předáváme rovnou
  }

  useEffect(() => {
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Přehled</h1>
      <Card>
        <div className="mb-4 flex flex-wrap gap-2">
          {PRESETS.map((p) => {
            const [f, t] = p.range()
            const active = f === from && t === to
            return (
              <Button
                key={p.label}
                variant={active ? 'primary' : 'secondary'}
                className="px-3 py-1.5"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            )
          })}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Od">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Do">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          {user?.isAdmin && (
            <div className="min-w-48">
              <Field label="Uživatel">
                <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Všichni</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.email}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
          <Button onClick={() => run()}>Zobrazit</Button>
        </div>
      </Card>

      {data && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold">Souhrn</h2>
            <span className="text-sm text-slate-500">Celkem {formatHours(data.totalSecs)}</span>
          </div>
          {data.clients.length === 0 ? (
            <p className="text-sm text-slate-400">Žádná data pro zvolené období.</p>
          ) : (
            <div className="space-y-5">
              {data.clients.map((c) => (
                <div key={c.clientId}>
                  <div className="mb-1 flex items-center justify-between border-b border-slate-200 pb-1">
                    <span className="font-medium">{c.clientName}</span>
                    <span className="text-sm text-slate-500">{formatHours(c.totalSecs)}</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[480px] text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase text-slate-400">
                          <th className="py-1">Projekt</th>
                          <th className="py-1 text-right">Hodiny</th>
                          <th className="py-1 text-right">Sazba</th>
                          <th className="py-1 text-right">Fakturace</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.projects.map((p) => (
                          <tr key={p.projectId} className="border-t border-slate-100">
                            <td className="py-1.5">
                              {p.projectName}
                              {!p.isBillable && <span className="ml-2 text-xs text-slate-400">(nefakturovatelné)</span>}
                            </td>
                            <td className="py-1.5 text-right font-mono">{p.billableHours.toFixed(2)}</td>
                            <td className="py-1.5 text-right text-slate-500">
                              {formatMoney(p.defaultRate, p.currency)}
                            </td>
                            <td className="py-1.5 text-right font-medium">
                              {p.isBillable ? formatMoney(p.billableAmount, p.currency) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
